/**
 * Module Rulebooks — Roadmap (aspirational, non-enforced).
 *
 * Strak-Trek Phase 3 — companion to the executable rulebooks.
 *
 * WHAT THIS IS
 * ────────────
 * Each module rulebook defines what the pipeline enforces TODAY. This file
 * defines what we INTEND to build — the design-language for the next
 * 100+ custom question types and 100+ visuals.
 *
 * These entries are NOT validated, NOT required, and NOT wired into the
 * pipeline. They live here as a roadmap so that:
 *   • Authors can see which interactions/visuals are coming and design
 *     seeds with them in mind.
 *   • Engineers have a curated backlog grounded in real family needs.
 *   • The admin "Regelboeken" tab surfaces a "🔮 Toekomstig" column next
 *     to the live didactic profile, making the trajectory visible.
 *
 * Sourced from a sweep of every module's seed families — each future type
 * or visual is tied to a concrete family pattern that exists today and
 * would benefit from a richer interaction or representation.
 *
 * WHEN TO PROMOTE A ROADMAP ENTRY
 *   1. Build the actual renderer / visual component.
 *   2. Register the type in src/governance/questionTypeDefinitions.ts
 *      or the visual in src/visuals/types.ts.
 *   3. Move the entry up into the live rulebook's `preferredTypes` /
 *      `preferredAidTypes` and (optionally) `customTypeRoles`.
 *   4. Delete the matching roadmap entry here.
 *
 * SCALE — we plan to grow this list well beyond 100 of each over the next
 * 12-18 months. Keep entries short: name + one-line didactic rationale +
 * a tier hint. The admin UI does the rest.
 */

import type { ModuleId } from '../../content'

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Aspirational future question type. Lives until promoted to a real
 * renderer + entry in `customTypeRoles`.
 */
export interface FutureQuestionTypeEntry {
  /** snake_case identifier matching the eventual QuestionType. */
  id: string
  /** Short Dutch label shown in admin UI. */
  label: string
  /** One-line didactic reason this type is valuable for the module. */
  rationale: string
  /** Tier window where the type is most useful. */
  tierWindow?: { min: number; max: number }
  /** Which families would primarily benefit (regex pattern strings). */
  targetFamilyPatterns?: string[]
}

/**
 * Aspirational future visual aid. Lives until promoted to a real
 * component + entry in `preferredAidTypes`.
 */
export interface FutureVisualEntry {
  /** camelCase identifier matching the eventual VisualAidType. */
  id: string
  /** Short Dutch label shown in admin UI. */
  label: string
  /** One-line didactic reason this visual is valuable. */
  rationale: string
  /** Whether this is typically used as stimulus or support (or both). */
  usage: 'stimulus' | 'support' | 'both'
  /** Which families would primarily benefit (regex pattern strings). */
  targetFamilyPatterns?: string[]
}

export interface ModuleRoadmap {
  futureQuestionTypes: FutureQuestionTypeEntry[]
  futureVisuals: FutureVisualEntry[]
}

// ─── GET — Getalbegrip ───────────────────────────────────────────────────────

const GET_ROADMAP: ModuleRoadmap = {
  futureQuestionTypes: [
    {
      id: 'fraction_strip_builder',
      label: 'Breukenstrip-bouwer',
      rationale:
        'Kind kleurt zelf delen van een breukenstrip — actief breuk-begrip i.p.v. passieve MC.',
      tierWindow: { min: 3, max: 12 },
      targetFamilyPatterns: ['^GET-BREUK-'],
    },
    {
      id: 'decimal_zoom',
      label: 'Kommagetal-zoom',
      rationale:
        'Zoom in op een stuk getallenlijn om kommagetal precies te plaatsen.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^GET-KOMMA-GETALLENLIJN'],
    },
    {
      id: 'percent_pie_builder',
      label: 'Procentcirkel-bouwer',
      rationale:
        'Stel een cirkeldiagram in op het juiste percentage; verbindt visuele en numerieke representatie.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^GET-PROC-'],
    },
    {
      id: 'ratio_recipe_scaler',
      label: 'Recept-schaler',
      rationale:
        'Schaal een recept op/af; betekenisvolle context voor verhoudingstabellen.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^GET-VERH-RECEPT', '^GET-VERH-'],
    },
    {
      id: 'fraction_equivalence_match',
      label: 'Gelijkwaardige breuken matchen',
      rationale:
        'Sleep gelijke breuken samen — bouwt vereenvoudig-intuitie zonder formele regel.',
      tierWindow: { min: 5, max: 12 },
      targetFamilyPatterns: [
        '^GET-BREUK-VEREENVOUDIG',
        '^GET-BREUK-UITBREIDEN',
      ],
    },
  ],
  futureVisuals: [
    {
      id: 'decimalNumberLine',
      label: 'Kommagetal-getallenlijn',
      rationale: 'Inzoombare getallenlijn voor decimale positie en afronden.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^GET-KOMMA-'],
    },
    {
      id: 'powerTower',
      label: 'Machten-toren',
      rationale: 'Machten gevisualiseerd als toren van grondtallen.',
      usage: 'support',
      targetFamilyPatterns: ['^GET-MACHT'],
    },
    {
      id: 'integerNumberLine',
      label: 'Heel-getal-getallenlijn met nul',
      rationale:
        'Symmetrische getallenlijn rond 0 — toont negatieve getallen als spiegelbeeld i.p.v. enkel thermometer-context.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^GET-NEGATIEF'],
    },
    {
      id: 'ratioGears',
      label: 'Verhoudings-tandwielen',
      rationale: 'Tandwiel-paar visualiseert verhouding als omwentelingsratio.',
      usage: 'support',
      targetFamilyPatterns: ['^GET-VERH-'],
    },
    {
      id: 'rootSpiral',
      label: 'Wortel-spiraal',
      rationale: 'Wortels en kwadraten als spiraal van Theodorus.',
      usage: 'support',
      targetFamilyPatterns: ['^GET-WORTEL'],
    },
  ],
}

// ─── REK — Basisrekenen ──────────────────────────────────────────────────────

const REK_ROADMAP: ModuleRoadmap = {
  futureQuestionTypes: [
    {
      id: 'mental_math_chain',
      label: 'Handig-rekenketen',
      rationale:
        'Kind kiest opeenvolgende stappen (splitsen → compenseren → optellen). Strategiebewustzijn.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^REK-HANDIG-'],
    },
    {
      id: 'times_table_gap_fill',
      label: 'Tafels-gat-vullen',
      rationale:
        'Tafel-raster met willekeurige gaps; fluency-training zonder herhalend MC.',
      tierWindow: { min: 1, max: 8 },
      targetFamilyPatterns: ['^REK-TAFELS-'],
    },
    {
      id: 'estimation_battle',
      label: 'Schatten vs. exact',
      rationale:
        'Kind kiest of schatten genoeg is óf exact rekenen nodig is — strategie-keuze.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^REK-SCHATTEN'],
    },
    {
      id: 'operation_chooser',
      label: 'Bewerking-kiezer',
      rationale:
        'Contextopgave waar het kind eerst de juiste bewerking kiest, dán uitrekent.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^REK-CONTEXT-'],
    },
    {
      id: 'order_of_ops_drag',
      label: 'Volgorde-bewerkingen drag',
      rationale:
        'Sleep haken/operatoren naar juiste plek — eindelijk de "Meneer Van Dale" visueel.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^REK-VOLGORDE'],
    },
    {
      id: 'cijferend_error_locate',
      label: 'Cijferfout-aanwijzen',
      rationale:
        'Kind krijgt een uitgewerkte cijferende som met één fout en wijst de kolom aan waar het mis ging — meta-cognitief en niet duplicaat van ColumnArithmetic.',
      tierWindow: { min: 3, max: 12 },
      targetFamilyPatterns: ['^REK-CIJF-'],
    },
  ],
  futureVisuals: [
    {
      id: 'compensationArrow',
      label: 'Compenseren-pijl',
      rationale:
        'Pijl op getallenlijn die "+1 / -1 truc" laat zien bij compenseren.',
      usage: 'support',
      targetFamilyPatterns: ['^REK-HANDIG-'],
    },
    {
      id: 'multiplicationArrayGrid',
      label: 'Tafels-array-grid',
      rationale:
        'Rechthoekig array-model (4×7 als 4 rijen van 7) — verbindt tafels met oppervlakte-betekenis.',
      usage: 'support',
      targetFamilyPatterns: ['^REK-TAFELS-', '^REK-CIJF-KEER'],
    },
    {
      id: 'partialProductsGrid',
      label: 'Gedeeltelijke producten',
      rationale:
        'Cijferend × via array-model (12×13 = 10×13 + 2×13) — solide overgang naar staartrekenen.',
      usage: 'support',
      targetFamilyPatterns: ['^REK-CIJF-KEER'],
    },
    {
      id: 'numberLineLeaps',
      label: 'Getallenlijn-sprongen',
      rationale:
        'Sprongen op getallenlijn maken handig rekenen (compenseren/splitsen) zichtbaar zonder formele notatie.',
      usage: 'support',
      targetFamilyPatterns: ['^REK-HANDIG-', '^REK-SCHATTEN'],
    },
  ],
}

// ─── MET — Metriek ───────────────────────────────────────────────────────────

const MET_ROADMAP: ModuleRoadmap = {
  futureQuestionTypes: [
    {
      id: 'area_decompose',
      label: 'Oppervlakte-opdelen',
      rationale:
        'Samengestelde figuur knippen in basisvormen voor oppervlakteberekening.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^MET-OPP-SAMENGESTELD'],
    },
    {
      id: 'unit_ladder_climb',
      label: 'Metriek-trap beklimmen',
      rationale:
        'Sleep waarde een trede omhoog/omlaag (km → m → cm) — kerngezichtsmodel.',
      tierWindow: { min: 3, max: 12 },
      targetFamilyPatterns: [
        '^MET-LEN-OMREKEN',
        '^MET-INH-OMREKEN',
        '^MET-GEW-OMREKEN',
        '^MET-OPP-OMREKENEN',
      ],
    },
    {
      id: 'area_grid_count',
      label: 'Oppervlakte tellen op grid',
      rationale:
        'Tel hokjes binnen een figuur op cm-raster — laagdrempelig fundament voor oppervlakteformules.',
      tierWindow: { min: 5, max: 12 },
      targetFamilyPatterns: ['^MET-OPP-', '^MET-MEETK-OPP'],
    },
    {
      id: 'volume_pour',
      label: 'Inhoud schenken',
      rationale:
        'Schenk virtueel water tussen vormen; bouwt conservation-of-volume intuïtie.',
      tierWindow: { min: 5, max: 12 },
      targetFamilyPatterns: ['^MET-INH-', '^MET-INH-LITER'],
    },
    {
      id: 'angle_protractor_drag',
      label: 'Gradenboog-meting',
      rationale:
        'Plaats digitale gradenboog op hoek en lees af — sluit aan op fysieke praktijk.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^MET-HOEK-METEN', '^MET-HOEK-TEKENEN'],
    },
  ],
  futureVisuals: [
    {
      id: 'metricStaircaseInteractive',
      label: 'Interactieve metriek-trap',
      rationale:
        'Volledig interactieve metriek-trap (uitbreiding van MetricStairsCompact).',
      usage: 'support',
      targetFamilyPatterns: ['^MET-.*-OMREKEN'],
    },
    {
      id: 'compositeShapeOverlay',
      label: 'Samengestelde-vorm-overlay',
      rationale: 'Toont subvormen met gekleurde overlay voor opdeling.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^MET-OPP-SAMENGESTELD'],
    },
    {
      id: 'formulaCard',
      label: 'Formule-kaart',
      rationale: 'Formule met benoemde variabelen + invul-velden.',
      usage: 'support',
      targetFamilyPatterns: ['^MET-OPP-', '^MET-OMTREK-', '^MET-INH-'],
    },
    {
      id: 'solidNetUnfold',
      label: '3D-uitslag',
      rationale:
        'Vouw een kubus/balk uit naar plat patroon — ruimtelijk inzicht.',
      usage: 'support',
      targetFamilyPatterns: ['^MET-INH-BLOK'],
    },
    {
      id: 'angleArcMarker',
      label: 'Hoek-boogje',
      rationale:
        'Kleine boog tussen twee zijden die de hoek visueel markeert — voor introductie hoeken zonder gradenboog.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^MET-HOEK-SOORTEN', '^MET-HOEK-METEN'],
    },
  ],
}

// ─── TIJD ────────────────────────────────────────────────────────────────────
// Audit §10.7.13 (mei 2026): patterns aangepast `^TGL-TIJD-` → `^TIJD-`. Sinds
// T8 Phase 2 (april 2026) gebruiken seeds + LDG kale `TIJD-*` familyIds.

const TIJD_ROADMAP: ModuleRoadmap = {
  futureQuestionTypes: [
    {
      id: 'duration_subtract',
      label: 'Tijdsduur-aftrekken',
      rationale:
        'Twee tijden invoeren, app berekent niet — kind doet via splitsen op uren/minuten.',
      tierWindow: { min: 3, max: 12 },
      targetFamilyPatterns: ['^TIJD-DUUR'],
    },
    {
      id: 'schedule_builder',
      label: 'Schema-bouwen',
      rationale:
        'Bouw een dagschema met sleepbare tijdblokken — planningsvaardigheid actief.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^TIJD-PLANNING', '^TIJD-SCHEMA'],
    },
    {
      id: 'speed_slider_solve',
      label: 'Snelheids-slider',
      rationale:
        'Sliders voor afstand/tijd, snelheid update live — bouwt v=s/t intuïtie.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^TIJD-SNELHEID', '^TIJD-AFSTAND'],
    },
    {
      id: 'analog_digital_match',
      label: 'Analoog-digitaal koppelen',
      rationale:
        'Sleep analoge klokken naar passende digitale tijden — klok-fluency over beide representaties.',
      tierWindow: { min: 3, max: 10 },
      targetFamilyPatterns: ['^TIJD-12-24', '^TIJD-ANALOOG'],
    },
  ],
  futureVisuals: [
    {
      id: 'clockDualHands',
      label: 'Dubbele-wijzer-klok',
      rationale: 'Begin- en eindtijd op één klok, voor duur-vragen.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^TIJD-DUUR'],
    },
    {
      id: 'timelineGantt',
      label: 'Gantt-tijdlijn',
      rationale: 'Tijdlijn met blokken voor planning en overlap.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^TIJD-PLANNING', '^TIJD-SCHEMA'],
    },
    {
      id: 'timezonePlanet',
      label: 'Tijdzone-globe',
      rationale: 'Wereldbol met dag/nacht-terminator en tijdzone-lijnen.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^TIJD-TIJDZONES'],
    },
    {
      id: 'weekendCounter',
      label: 'Werkdagen-strip',
      rationale:
        'Tijdlijn-strip die werkdagen en weekend kleurcodeert — specifiek voor planning-vragen.',
      usage: 'support',
      targetFamilyPatterns: ['^TIJD-PLANNING', '^TIJD-KALENDER'],
    },
    {
      id: 'pendulumSpeed',
      label: 'Snelheidsslinger',
      rationale: 'Slinger toont snelheid via tempo — fysieke intuïtie.',
      usage: 'support',
      targetFamilyPatterns: ['^TIJD-SNELHEID'],
    },
  ],
}

// ─── GELD ────────────────────────────────────────────────────────────────────
// Audit §10.7.13 (mei 2026): patterns aangepast `^TGL-GELD-` → `^GELD-`. Sinds
// T8 Phase 2 (april 2026) gebruiken seeds + LDG kale `GELD-*` familyIds.

const GELD_ROADMAP: ModuleRoadmap = {
  futureQuestionTypes: [
    {
      id: 'discount_slider',
      label: 'Korting-schuif',
      rationale:
        'Slider voor kortingspercentage, eindprijs update live — verbindt % en €.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^GELD-KORTING', '^GELD-KOOPJES'],
    },
    {
      id: 'receipt_check',
      label: 'Bon-controle',
      rationale:
        'Vind de fout op een kassabon — realistische foutdetectie + tellen.',
      tierWindow: { min: 5, max: 12 },
      targetFamilyPatterns: ['^GELD-BOODSCHAPPEN'],
    },
    {
      id: 'savings_goal_plan',
      label: 'Spaardoel-planner',
      rationale:
        'Stel doel, weekbedrag, einddatum in — bouwt financiële planning vroeg op.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^GELD-SPAREN'],
    },
    {
      id: 'change_back_assembler',
      label: 'Wisselgeld bouwen',
      rationale:
        'Bouw het wisselgeld met zo min mogelijk munten — uitbreiding op MoneyAssembler richting teruggeef-strategie.',
      tierWindow: { min: 3, max: 10 },
      targetFamilyPatterns: ['^GELD-TERUG', '^GELD-WISSELEN'],
    },
    {
      id: 'tip_calculator',
      label: 'Fooi-rekenmachine',
      rationale:
        'Bereken 10/15/20% fooi op een bedrag — concrete %-toepassing die kinderen herkennen uit restaurant-context.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^GELD-BOODSCHAPPEN', '^GELD-BTW'],
    },
  ],
  futureVisuals: [
    {
      id: 'priceTagStack',
      label: 'Prijskaarten-stapel',
      rationale: 'Stapel prijskaarten met "was/nu" voor kortings-vragen.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^GELD-KORTING', '^GELD-PRIJS-PER'],
    },
    {
      id: 'savingsJar',
      label: 'Spaarpot',
      rationale: 'Visuele spaarpot met vulniveau, koppelt € en voortgang.',
      usage: 'support',
      targetFamilyPatterns: ['^GELD-SPAREN'],
    },
    {
      id: 'discountSticker',
      label: 'Kortings-sticker',
      rationale: 'Diagonale sticker-overlay met -X% op product.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^GELD-KORTING'],
    },
    {
      id: 'weeklyBudgetCalendar',
      label: 'Week-budget-kalender',
      rationale:
        'Kalender met dagelijkse uitgaven over een week — verbindt budget met planning.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^GELD-BUDGET', '^GELD-SPAREN'],
    },
  ],
}

// ─── SPR — Spelling Regels ───────────────────────────────────────────────────

const SPR_ROADMAP: ModuleRoadmap = {
  futureQuestionTypes: [
    {
      id: 'syllable_split',
      label: 'Lettergrepen-knippen',
      rationale:
        'Klik tussen letters om lettergrepen te markeren — verplicht voor open/gesloten-regel.',
      tierWindow: { min: 3, max: 10 },
      targetFamilyPatterns: ['^SPR-KLANK-'],
    },
    {
      id: 'letter_swap_highlight',
      label: 'Letter-fout aanwijzen',
      rationale:
        'Klik de foute letter aan in een woord — locatiebewust foutdetectie.',
      tierWindow: { min: 3, max: 12 },
      targetFamilyPatterns: ['^SPR-WEET-', '^SPR-KLANK-'],
    },
    {
      id: 'compound_builder',
      label: 'Samenstelling bouwen',
      rationale:
        'Combineer twee woorden met de juiste tussen-n/s — regel oefenen.',
      tierWindow: { min: 5, max: 12 },
      targetFamilyPatterns: ['^SPR-SAM-'],
    },
    {
      id: 'accent_picker',
      label: 'Accent kiezen',
      rationale:
        'Kies juiste accent (é/è/ë) — accuraat voor leen- en trema-woorden.',
      tierWindow: { min: 5, max: 12 },
      targetFamilyPatterns: ['^SPR-ACCENT', '^SPR-TREMA-MV'],
    },
    {
      id: 'loanword_origin_match',
      label: 'Leenwoord-herkomst koppelen',
      rationale:
        'Koppel leenwoord aan herkomsttaal — context voor spelling-uitzonderingen.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^SPR-LEEN-'],
    },
  ],
  futureVisuals: [
    {
      id: 'letterTile',
      label: 'Lettertegels',
      rationale: 'Sleepbare lettertegels voor letter-volgorde-vragen.',
      usage: 'support',
      targetFamilyPatterns: ['^SPR-WEET-', '^SPR-LEESTEKEN-'],
    },
    {
      id: 'openClosedHouse',
      label: 'Open/gesloten-huis',
      rationale:
        'Lettergreep als huis-metafoor (open = deur open) — kleuter-vriendelijk model.',
      usage: 'support',
      targetFamilyPatterns: ['^SPR-KLANK-JAGER', '^SPR-KLANK-BAKKER'],
    },
    {
      id: 'stressMarker',
      label: 'Klemtoon-streepje',
      rationale:
        'Streepje of accent boven beklemtoonde lettergreep — apart van lettergreep-scheiding, voor klemtoon-bewustzijn.',
      usage: 'support',
      targetFamilyPatterns: ['^SPR-ACCENT', '^SPR-KLANK-'],
    },
    {
      id: 'tremaDots',
      label: 'Trema-stippen',
      rationale: 'Stippen-animatie over klinker om trema-regel uit te leggen.',
      usage: 'support',
      targetFamilyPatterns: ['^SPR-TREMA-'],
    },
    {
      id: 'etymologyTimeline',
      label: 'Etymologie-tijdlijn',
      rationale: 'Tijdlijn met herkomst-talen — context voor leenwoorden.',
      usage: 'support',
      targetFamilyPatterns: ['^SPR-LEEN-'],
    },
  ],
}

// ─── SWW — Spelling Werkwoorden ──────────────────────────────────────────────

const SWW_ROADMAP: ModuleRoadmap = {
  futureQuestionTypes: [
    {
      id: 'stam_builder',
      label: 'Stam-bouwer',
      rationale:
        'Leid stam stap-voor-stap af uit infinitief; expliciteert de regel.',
      tierWindow: { min: 3, max: 10 },
      targetFamilyPatterns: ['^SWW-TT-STAM'],
    },
    {
      id: 'kofschip_flow',
      label: 'Kofschip-beslisboom',
      rationale:
        'Stap door de beslisboom (eindigt op kofschip-letter?) — meta-cognitief.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^SWW-VT-ZWAK-KOF', '^SWW-VD-KOF'],
    },
    {
      id: 'conjugation_grid',
      label: 'Vervoegingsraster',
      rationale:
        'Vul TT/VT/VD-grid voor één werkwoord — overzicht boven losse vragen.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^SWW-TT-', '^SWW-VT-'],
    },
    {
      id: 'kofschip_rule_pick',
      label: 'Welke regel?',
      rationale:
        'Kind kiest welke regel van toepassing is (kofschip / d-stam / sterk) — meta-cognitieve regel-bewustzijn boven uitkomst-keuze.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^SWW-VT-', '^SWW-VD-'],
    },
    {
      id: 'irregular_verb_match',
      label: 'Onregelmatige werkwoorden koppelen',
      rationale:
        'Koppel infinitief aan VT/VD-vorm van onregelmatige werkwoorden — pure mapping-oefening, anders dan flow-renderer.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: [
        '^SWW-VT-ONREGELMATIG',
        '^SWW-VT-STERK',
        '^SWW-VD-STERK',
      ],
    },
  ],
  futureVisuals: [
    {
      id: 'kofschipShip',
      label: 'Kofschip-boot',
      rationale:
        'Visuele kofschip-boot met letter-vlaggen — geanimeerde uitbreiding van KofschipMnemonic.',
      usage: 'support',
      targetFamilyPatterns: ['^SWW-VT-ZWAK-KOF', '^SWW-VD-KOF'],
    },
    {
      id: 'verbStemDiagram',
      label: 'Stam-boom',
      rationale: 'Boom: infinitief → stam → vervoegingen per persoonsvorm.',
      usage: 'support',
      targetFamilyPatterns: ['^SWW-TT-STAM', '^SWW-VT-STAM'],
    },
    {
      id: 'personPronounMatrix',
      label: 'Persoonsvorm-matrix',
      rationale:
        'Matrix ik/jij/hij × TT/VT/VD — overzicht van vervoeging op één blik.',
      usage: 'support',
      targetFamilyPatterns: ['^SWW-TT-', '^SWW-VT-'],
    },
    {
      id: 'verbStemRuler',
      label: 'Stam-meetlat',
      rationale:
        'Visuele meetlat die stam + uitgang naast elkaar legt — letterlijk afmeten van vervoeging.',
      usage: 'support',
      targetFamilyPatterns: ['^SWW-TT-STAM', '^SWW-TT-DSTAM'],
    },
    {
      id: 'pastFormGalleryStrong',
      label: 'Sterke-werkwoorden-galerij',
      rationale:
        'Kaartgalerij van sterke werkwoorden — visueel geheugen-anker.',
      usage: 'support',
      targetFamilyPatterns: ['^SWW-VT-STERK', '^SWW-VD-STERK'],
    },
  ],
}

// ─── TAA — Taalbegrip ────────────────────────────────────────────────────────

const TAA_ROADMAP: ModuleRoadmap = {
  futureQuestionTypes: [
    {
      id: 'tree_diagram_build',
      label: 'Zinsboom bouwen',
      rationale:
        'Bouw zinsontleding als boom; structureel inzicht boven categorie-MC.',
      tierWindow: { min: 9, max: 16 },
      targetFamilyPatterns: ['^TAA-ONT-'],
    },
    {
      id: 'summary_sentence_pick',
      label: 'Beste samenvatting kiezen',
      rationale:
        'Kies tussen 3 samenvattingen welke kort én compleet is — gesloten variant van samenvatten zonder open AI-grading.',
      tierWindow: { min: 7, max: 16 },
      targetFamilyPatterns: ['^TAA-LEZEN-SAMENVATTEN', '^TAA-LEZEN-KERN'],
    },
    {
      id: 'reference_arrow_draw',
      label: 'Verwijspijl tekenen',
      rationale:
        'Teken pijl van verwijswoord naar antecedent — locatiebewust referentie-begrip.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^TAA-LEZEN-VERW'],
    },
    {
      id: 'argument_structure_map',
      label: 'Argumentatie-schema',
      rationale:
        'Vul claim / argument / weerwoord in op schema — argumentatie-leer.',
      tierWindow: { min: 9, max: 16 },
      targetFamilyPatterns: ['^TAA-ARGUMENTATIE'],
    },
    {
      id: 'text_purpose_match',
      label: 'Tekstdoel matchen',
      rationale:
        'Koppel tekstfragment aan doel (informeren/overtuigen/amuseren) — specifieker dan algemene tekstsoort en concreter dan categorize.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^TAA-LEZEN-DOEL'],
    },
  ],
  futureVisuals: [
    {
      id: 'wordClassMap',
      label: 'Woordsoort-kaart',
      rationale: 'Volledige zin met elke woordsoort in eigen kleur/badge.',
      usage: 'support',
      targetFamilyPatterns: ['^TAA-SOORT-'],
    },
    {
      id: 'clauseChunker',
      label: 'Hoofd/bijzin-chunks',
      rationale:
        'Visuele scheiding hoofdzin/bijzin via gekleurde brackets — andere grain dan SentenceDiagram woordsoort-niveau.',
      usage: 'support',
      targetFamilyPatterns: ['^TAA-ONT-HOOFDZIN', '^TAA-ONT-BIJZIN'],
    },
    {
      id: 'argumentScaffold',
      label: 'Argument-stellage',
      rationale: 'Stellage met "stelling boven, argumenten eronder" structuur.',
      usage: 'support',
      targetFamilyPatterns: ['^TAA-ARGUMENTATIE'],
    },
    {
      id: 'coherenceWeb',
      label: 'Tekstcohesie-web',
      rationale:
        'Pijlen tussen verwijswoorden + voornaamwoorden door een passage — visualiseert hoe tekst aan elkaar hangt.',
      usage: 'support',
      targetFamilyPatterns: ['^TAA-LEZEN-VERB', '^TAA-LEZEN-STRUCTUUR'],
    },
    {
      id: 'referenceWebOverlay',
      label: 'Verwijs-web',
      rationale:
        'Overlay van verwijspijlen over een passage — visualiseert tekstcohesie.',
      usage: 'support',
      targetFamilyPatterns: ['^TAA-LEZEN-VERW'],
    },
  ],
}

// ─── STU — Studievaardig ─────────────────────────────────────────────────────

const STU_ROADMAP: ModuleRoadmap = {
  futureQuestionTypes: [
    {
      id: 'mindmap_node_build',
      label: 'Mindmap-bouwen',
      rationale:
        'Sleep nodes en verbinding; mindmap als productieve i.p.v. receptieve taak.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^STU-MINDMAP'],
    },
    {
      id: 'table_query_drilldown',
      label: 'Tabel-drilldown',
      rationale: 'Filter en drill down in tabel — meer dan losse cel-vragen.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^STU-TABEL'],
    },
    {
      id: 'route_planner_step',
      label: 'Route-planner',
      rationale:
        'Plan route met afstandsoptimalisatie — kaart + rekenen samen.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^STU-ROUTE', '^STU-KAART'],
    },
    {
      id: 'index_lookup_race',
      label: 'Register-zoeken (tijd)',
      rationale:
        'Snel opzoeken in alfabetisch register tegen de klok — studievaardigheid + alfabet.',
      tierWindow: { min: 3, max: 10 },
      targetFamilyPatterns: ['^STU-ALFABET', '^STU-WOORDENBOEK'],
    },
    {
      id: 'chart_compare_pair',
      label: 'Grafiek-vergelijken',
      rationale: 'Vergelijk twee grafieken naast elkaar — meta-grafiek-vragen.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^STU-GRAFIEK', '^STU-STAAF', '^STU-LIJN'],
    },
  ],
  futureVisuals: [
    {
      id: 'interactiveMindmap',
      label: 'Interactieve mindmap',
      rationale: 'Sleepbare mindmap met levende nodes.',
      usage: 'both',
      targetFamilyPatterns: ['^STU-MINDMAP'],
    },
    {
      id: 'floorplanMap',
      label: 'Plattegrond',
      rationale: 'Plattegrond (school/winkel) voor route- en plaatsvragen.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^STU-KAART', '^STU-ROUTE'],
    },
    {
      id: 'dictionaryPage',
      label: 'Woordenboekpagina',
      rationale: 'Volledige woordenboekpagina-spread voor lookup-vaardigheden.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^STU-WOORDENBOEK'],
    },
    {
      id: 'tableOfContentsTree',
      label: 'Inhoudsopgave-boom',
      rationale: 'Inhoudsopgave als uitklap-boomstructuur.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^STU-INHOUDSOPGAVE'],
    },
    {
      id: 'dualAxisChart',
      label: 'Dubbele-as-grafiek',
      rationale:
        'Twee y-assen op één grafiek (bijv. temperatuur + neerslag) — uitdagende didactische taak voor groep 8 die echte data-leesvaardigheid vraagt.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^STU-GRAFIEK', '^STU-LIJN', '^STU-DIAGRAM'],
    },
  ],
}

// ─── TOPO ────────────────────────────────────────────────────────────────────

const TOPO_ROADMAP: ModuleRoadmap = {
  futureQuestionTypes: [
    {
      id: 'map_quiz_speed',
      label: 'Kaart-snelheidsquiz',
      rationale:
        'Meerdere targets aanwijzen op kaart tegen de klok — fluency-trainer.',
      tierWindow: { min: 5, max: 14 },
      targetFamilyPatterns: ['^TOPO-'],
    },
    {
      id: 'border_neighbor_pick',
      label: 'Buurland aanwijzen',
      rationale: 'Klik op alle buurlanden van X — relationele topografie.',
      tierWindow: { min: 5, max: 12 },
      targetFamilyPatterns: ['^TOPO-EU-BUURLANDEN', '^TOPO-NL-BUURLANDEN'],
    },
    {
      id: 'river_sequence_order',
      label: 'Rivier-volgorde',
      rationale:
        'Zet steden op volgorde langs een rivier van bron naar monding — ruimtelijk inzicht zonder vrije tekening.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^TOPO-EU-RIVIEREN', '^TOPO-WERELD-RIVIER'],
    },
    {
      id: 'climate_zone_match',
      label: 'Klimaatzone-koppelen',
      rationale:
        'Sleep locatie naar klimaatzone — verbindt geografie en klimaat.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^TOPO-EU-KLIMAAT', '^TOPO-WERELD-KLIMAAT'],
    },
    {
      id: 'mountain_extent_picker',
      label: 'Gebergte-landen kiezen',
      rationale:
        'Kies welke landen een gebergteketen doorkruist — relationele topografie zonder tekenwerk.',
      tierWindow: { min: 7, max: 14 },
      targetFamilyPatterns: ['^TOPO-EU-GEBERGTE', '^TOPO-WERELD-GEBERGTE'],
    },
  ],
  futureVisuals: [
    {
      id: 'riverFlowOverlay',
      label: 'Rivierstroom-overlay',
      rationale: 'Geanimeerde rivierstroom met richting + delta.',
      usage: 'support',
      targetFamilyPatterns: ['^TOPO-.*-RIVIER'],
    },
    {
      id: 'climateZoneOverlay',
      label: 'Klimaatzone-overlay',
      rationale: 'Gekleurde klimaatzones over kaart (Köppen).',
      usage: 'stimulus',
      targetFamilyPatterns: ['^TOPO-.*-KLIMAAT'],
    },
    {
      id: 'elevationCrosssection',
      label: 'Hoogteprofiel',
      rationale: 'Doorsnede van hoogte langs een lijn — fysische geografie.',
      usage: 'support',
      targetFamilyPatterns: ['^TOPO-.*-GEBERGTE', '^TOPO-NL-LANDSCHAP'],
    },
    {
      id: 'flagBadgeGrid',
      label: 'Vlaggen-raster',
      rationale: 'Raster van vlaggen voor matching-vragen.',
      usage: 'support',
      targetFamilyPatterns: ['^TOPO-EU-LAND', '^TOPO-WERELD-LANDEN'],
    },
    {
      id: 'layeredMap',
      label: 'Lagen-kaart',
      rationale:
        'Toggle tussen politieke / fysische / klimaat-lagen op dezelfde basemap — echte atlas-vaardigheid.',
      usage: 'stimulus',
      targetFamilyPatterns: ['^TOPO-'],
    },
  ],
}

// ─── Registry ────────────────────────────────────────────────────────────────

const ROADMAPS_REGISTRY: Record<ModuleId, ModuleRoadmap> = {
  Getalbegrip: GET_ROADMAP,
  Basisrekenen: REK_ROADMAP,
  Metriek: MET_ROADMAP,
  Tijd: TIJD_ROADMAP,
  Geld: GELD_ROADMAP,
  SpellingRegel: SPR_ROADMAP,
  SpellingWW: SWW_ROADMAP,
  Taalbegrip: TAA_ROADMAP,
  Studievaardig: STU_ROADMAP,
  Topo: TOPO_ROADMAP,
}

/**
 * Get the roadmap (future types + visuals) for a module.
 */
export function getModuleRoadmap(
  moduleId: ModuleId | string | undefined,
): ModuleRoadmap | null {
  if (!moduleId) return null
  return ROADMAPS_REGISTRY[moduleId as ModuleId] ?? null
}

/**
 * Total counts surfaced in admin diagnostics — at-a-glance roadmap size.
 */
export function countRoadmapEntries(): {
  futureQuestionTypes: number
  futureVisuals: number
} {
  let qt = 0
  let v = 0
  for (const rm of Object.values(ROADMAPS_REGISTRY)) {
    qt += rm.futureQuestionTypes.length
    v += rm.futureVisuals.length
  }
  return { futureQuestionTypes: qt, futureVisuals: v }
}
